// L'e-mail du club à ses partenaires (D-147) : l'invitation au match, sa relance, les nouvelles du trimestre, les vœux,
// le merci, la fin de saison, le rappel d'une promesse de don ; le lien de son espace partenaire, demandé depuis la page
// d'un QR de membre (D-156). Un seul rendu, aux couleurs du club : « <Club> via
// Arceau », le logo, la carte du match, les attentions en liste, « Je viens » / « Je ne peux pas » (la page de réponse
// /l/<jeton>, qui enregistre depuis le navigateur), signé par le membre du bureau qui envoie (les réponses vont chez lui,
// Reply-To), le pied « Ne plus recevoir ces messages ». Vouvoiement.
//
// Logique pure, sans import : push-dispatch l'utilise pour envoyer (HTML + texte), l'app pour l'aperçu de la feuille
// « Inviter » et des nouvelles (le même rendu). Testée sans réseau (partner-mail.test.ts).

export type PartnerMailKind = 'invitation' | 'relance' | 'nouvelles' | 'voeux' | 'merci' | 'fin_saison' | 'promesse' | 'espace';
export interface MailGift { title: string; kind?: string | null; is_bon?: boolean | null; preset?: string | null }
export interface PartnerMailIn {
  kind: PartnerMailKind;
  token: string;                                   // le jeton de l'e-mail : « Ne plus recevoir ces messages »
  club: { name: string; color: string | null; logo: string | null; city: string | null };
  partner: { name: string; civility: string | null; contact: string | null };
  signer: { name: string | null; title: string | null };
  /** Les réponses arrivent chez le signataire (Reply-To). */
  replyTo: boolean;
  invitation?: {
    token: string; title: string; day: string; time: string; location: string | null; competition: string | null;
    host: string | null; gifts: MailGift[]; mic: boolean;
  } | null;
  news?: { title: string; body: string } | null;
  message?: { title: string; detail: string | null } | null;
  pledge?: { amount_cents: number; season: string; since: string; partner?: string } | null;
  /** D-156 : le lien privé de son espace (/p/<jeton>). */
  space?: { token: string } | null;
}
export interface InlineImage { cid: string; contentType: string; data: string }
export interface BuiltPartnerMail {
  subject: string; fromName: string; html: string; inner: string; text: string; footer: string;
  unsubscribe: string; answer: { yes: string; no: string } | null; inline: InlineImage[];
}

// -----------------------------------------------------------------------------
// Mots
// -----------------------------------------------------------------------------
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
/** « dimanche 8 novembre » (une date ISO, sans fuseau). */
export function longDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d!));
  return `${DAYS[t.getUTCDay()]} ${d === 1 ? '1er' : d} ${MONTHS[m! - 1]}`;
}
/** « Madame Martin » (le nom de famille), le nom tel quel sans civilité, sinon rien. */
export function greeting(civility: string | null | undefined, contact: string | null | undefined): string {
  const name = (contact ?? '').trim().replace(/\s+/g, ' ');
  if (!name) return '';
  return civility ? `${civility} ${name.split(' ').slice(-1)[0]}` : name;
}
/** « le BC Gimont », « l’ASC Aureilhan », « Auch » (comme src/domain/lien.ts). */
export function withArticle(club: string): string {
  const c = club.trim();
  if (/^(basket|club|cercle|stade|racing|foyer|bc|cb|sporting)\b/i.test(c)) return `le ${c}`;
  if (/^(as|us|es|asc|ascl|uscl|ab|union|association|entente|étoile|elan|élan|avenir|alliance)\b/i.test(c)) return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `l’${c}` : `la ${c}`;
  return c;
}
/** « du Basket Club de Rivebelle », « de l’ASC Aureilhan », « de Lou Basket ». */
export function ofClub(club: string): string {
  const a = withArticle(club);
  return a.startsWith('le ') ? `du ${a.slice(3)}` : a.startsWith('la ') ? `de la ${a.slice(3)}` : a.startsWith('l’') ? `de ${a}` : `de ${a}`;
}
/** « pour le Basket Club de Rivebelle ». */
const forClub = (club: string) => `pour ${withArticle(club)}`;
/** « SF2 – BC Gimont » → l'équipe et l'adversaire. */
function splitTitle(title: string): { team: string; opponent: string } {
  const i = title.indexOf(' – ');
  return i < 0 ? { team: '', opponent: title } : { team: title.slice(0, i), opponent: title.slice(i + 3) };
}
const COMPETITION: Record<string, string> = { championnat: 'Championnat', coupe: 'Coupe', phase_finale: 'Phase finale', brassage: 'Brassage', amical: 'Match amical' };
/** « 100 € », « 149,50 € ». */
export const euros = (cents: number) => `${Math.floor(cents / 100)}${cents % 100 ? `,${String(cents % 100).padStart(2, '0')}` : ''} €`;

/** Les attentions, en liste, telles que le partenaire les lit (« Une boisson à la buvette »). */
export function giftLines(gifts: MailGift[], mic: boolean, partner: string): string[] {
  const words: Record<string, string> = {
    boisson: 'Une boisson à la buvette', gaufre: 'Une gaufre ou une crêpe', place: 'Une place réservée en tribune',
    presente: `Le match présenté par ${partner}`, photo: `Une photo avec l’équipe et une pancarte « Merci ${partner} »`,
    maillot: 'Un maillot dédicacé par l’équipe', repas: 'Le repas d’après-match avec le bureau',
  };
  return [...gifts.filter(g => g.kind !== 'merci').map(g => (g.preset && words[g.preset]) || cap(g.title.trim())), ...(mic ? ['Un merci au micro, à la mi-temps'] : [])];
}

// -----------------------------------------------------------------------------
// Couleurs du club (comme src/domain/brand.ts : le fond tel quel, l'encre lisible, le texte en couleur assombri)
// -----------------------------------------------------------------------------
const HEX = /^#[0-9a-fA-F]{6}$/;
const rgb = (h: string) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
const lum = (h: string) => {
  const [r, g, b] = rgb(h).map(v => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const ratio = (a: string, b: string) => { const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p) as [number, number]; return (x + 0.05) / (y + 0.05); };
const mixHex = (a: string, b: string, t: number) => '#' + rgb(a).map((v, i) => Math.round(v + (rgb(b)[i]! - v) * t).toString(16).padStart(2, '0')).join('').toUpperCase();
export function mailColors(color: string | null | undefined): { fill: string; ink: string; text: string } {
  const fill = color && HEX.test(color) ? color.toUpperCase() : '#E8590C';
  const ink = ratio('#FFFFFF', fill) >= ratio('#16181D', fill) ? '#FFFFFF' : '#16181D';
  let text = '#000000';
  for (let t = 0; t <= 1.0001; t += 0.05) { const c = mixHex(fill, '#000000', t); if (ratio(c, '#FFFFFF') >= 4.5) { text = c; break; } }
  return { fill, ink, text };
}

// -----------------------------------------------------------------------------
// Rendu
// -----------------------------------------------------------------------------
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const safeUrl = (u: string) => (/^https?:\/\/[^\s"<>]+$/.test(u) ? u : '#');
const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif";
const DISPLAY = "Anton, Impact, 'Arial Narrow', 'Helvetica Neue', Arial, sans-serif";
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const monogram = (name: string) => (name.replace(/[^\p{L}\p{N}\s'-]/gu, ' ').split(/[\s'-]+/).filter(w => w.length > 1).map(w => w[0]!).join('').slice(0, 3) || name.slice(0, 2)).toUpperCase();

/** Le logo : une image embarquée dans l'e-mail (cid:) quand le club l'a déposée, son adresse https, sinon ses initiales. */
function logoOf(logo: string | null, mode: 'send' | 'preview'): { src: string | null; inline: InlineImage | null } {
  if (!logo) return { src: null, inline: null };
  const m = /^data:(image\/(?:png|jpeg|webp));base64,([A-Za-z0-9+/]+=*)$/.exec(logo);
  if (m) return mode === 'preview' ? { src: logo, inline: null } : { src: 'cid:logo-club@arceau', inline: { cid: 'logo-club@arceau', contentType: m[1]!, data: m[2]! } };
  return /^https:\/\/[^\s"<>]+$/.test(logo) ? { src: logo, inline: null } : { src: null, inline: null };
}

export function partnerMail(m: PartnerMailIn, appUrl: string, mode: 'send' | 'preview' = 'send'): BuiltPartnerMail {
  const base = appUrl.replace(/\/+$/, '');
  const c = mailColors(m.club.color);
  const club = m.club.name.trim();
  const hello = greeting(m.partner.civility, m.partner.contact);
  const unsubscribe = `${base}/l/${m.token}?stop=1`;
  const logo = logoOf(m.club.logo, mode);
  const signer = (m.signer.name ?? '').trim();
  const title = (m.signer.title ?? '').trim();

  // --- Les blocs (HTML et texte côte à côte) ---
  const htmlParts: string[] = [];
  const textParts: string[] = [];
  const p = (s: string, style = '') => { htmlParts.push(`<p style="margin:0 0 16px;${style}">${s}</p>`); };
  const para = (s: string) => { p(esc(s).replace(/\n/g, '<br>')); textParts.push(s); };
  const kicker = (s: string) => `<p style="margin:0 0 6px;font-family:${MONO};font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:${c.text}">${esc(s)}</p>`;
  const list = (items: string[]) => {
    htmlParts.push(`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 18px">${items.map(x =>
      `<tr><td valign="top" style="width:26px;padding:3px 0;color:${c.text};font-weight:800;font-size:17px">✓</td><td style="padding:3px 0;font-size:15.5px">${esc(x)}</td></tr>`).join('')}</table>`);
    textParts.push(items.map(x => `  • ${x}`).join('\n'));
  };
  const card = (head: string, big: string | null, rows: string[], lines: string[] = []) => {
    htmlParts.push(`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 18px;border:2px solid ${c.fill};border-radius:14px">
      <tr><td style="padding:16px 18px">${kicker(head)}${big ? `<p style="margin:0 0 6px;font-family:${DISPLAY};font-size:30px;line-height:1.05;text-transform:uppercase;color:#1D2128">${esc(big)}</p>` : ''}
      ${rows.length ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0">${rows.join('')}</table>` : ''}
      ${lines.map(l => `<p style="margin:2px 0;font-size:14.5px">${esc(l)}</p>`).join('')}</td></tr></table>`);
  };
  const row = (icon: string, inner: string) => `<tr><td valign="top" style="width:24px;padding:2px 0;color:${c.text};font-weight:700">${icon}</td><td style="padding:2px 0;font-size:15px">${inner}</td></tr>`;
  const buttons = (yes: string, no: string) => {
    htmlParts.push(`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 12px"><tr>
      <td width="50%" style="padding-right:5px"><a href="${esc(yes)}" style="display:block;text-align:center;padding:14px 10px;border-radius:12px;background:${c.fill};color:${c.ink};font-weight:700;font-size:16px;text-decoration:none">Je viens</a></td>
      <td width="50%" style="padding-left:5px"><a href="${esc(no)}" style="display:block;text-align:center;padding:12px 10px;border-radius:12px;border:2px solid #C9CED6;color:#1D2128;font-weight:700;font-size:16px;text-decoration:none">Je ne peux pas</a></td></tr></table>`);
    textParts.push(`Je viens : ${yes}\nJe ne peux pas : ${no}`);
  };
  const small = (s: string) => { p(esc(s), 'font-size:13px;color:#5C6472'); textParts.push(s); };

  let subject = '', sub = '', pre = '';
  let answer: BuiltPartnerMail['answer'] = null;
  const helloLine = `Bonjour${hello ? ` ${hello}` : ''},`;
  para(helloLine);

  const inv = m.invitation;
  if ((m.kind === 'invitation' || m.kind === 'relance') && inv) {
    const { team, opponent } = splitTitle(inv.title);
    const day = cap(longDay(inv.day));
    const link = `${base}/l/${inv.token}`;
    answer = { yes: `${link}?r=oui`, no: `${link}?r=non` };
    sub = 'vous invite au match';
    pre = `${inv.title} · ${day} à ${inv.time} · Je viens ou je ne peux pas ?`;
    if (m.kind === 'invitation') {
      subject = `Invitation au match ${inv.title} · ${day}`;
      para(`${team ? `Les ${team} reçoivent ${withArticle(opponent)}` : `Le club reçoit ${withArticle(opponent)}`}. Nous serions ravis de vous y accueillir.`);
      const head = ['Match à domicile', inv.competition ? COMPETITION[inv.competition] ?? '' : ''].filter(Boolean).join(' · ');
      card(head, inv.title, [
        row('◷', `<b>${esc(day)}</b> · coup d’envoi à ${esc(inv.time)}`),
        row('⌖', esc(inv.location || 'Au gymnase du club')),
        ...(inv.host ? [row('☺', `${esc(inv.host)} vous attendra à l’entrée`)] : []),
      ]);
      textParts.push([inv.title, `${day} · coup d’envoi à ${inv.time}`, inv.location || 'Au gymnase du club', inv.host ? `${inv.host} vous attendra à l’entrée.` : ''].filter(Boolean).join('\n'));
      const gifts = giftLines(inv.gifts ?? [], inv.mic, m.partner.name);
      if (gifts.length) {
        htmlParts.push(kicker('Le club vous offre'));
        textParts.push('Le club vous offre :');
        list(gifts);
      }
    } else {
      subject = `Rappel : invitation au match ${inv.title} · ${day}`;
      para(`Petit rappel : ${team ? `les ${team} reçoivent ${withArticle(opponent)}` : `le club reçoit ${withArticle(opponent)}`} ${longDay(inv.day)} à ${inv.time}${inv.location ? ` (${inv.location})` : ''}, et nous serions ravis de vous y accueillir.`);
    }
    p(`<b>Pouvez-vous nous dire si vous venez ?</b>`);
    textParts.push('Pouvez-vous nous dire si vous venez ?');
    buttons(answer.yes, answer.no);
    small('Un clic suffit, sans compte. Vous pourrez changer d’avis jusqu’au coup d’envoi.');
  } else if (m.kind === 'nouvelles' && m.news) {
    subject = `${m.news.title} · ${club}`;
    sub = m.news.title;
    pre = `Les nouvelles du ${club}`;
    // Le brouillon commence par « Bonjour, » et finit par « Le bureau du … » : la salutation et la signature sont celles de l'e-mail.
    const blocks = m.news.body.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean)
      .filter((b, i, all) => !(i === 0 && /^Bonjour\b/i.test(b)) && !(i === all.length - 1 && /^Le bureau d/i.test(b)));
    for (const b of blocks) {
      const res = /^Les résultats\s*:\s*\n?([\s\S]*)$/i.exec(b);
      if (res) {
        const lines = res[1]!.split('\n').map(l => l.replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);
        card('Les résultats', null, [], lines);
        textParts.push(`Les résultats :\n${lines.map(l => `  • ${l}`).join('\n')}`);
      } else para(b);
    }
  } else if ((m.kind === 'voeux' || m.kind === 'merci' || m.kind === 'fin_saison') && m.message) {
    subject = `${m.message.title} · ${club}`;
    sub = m.message.title;
    pre = m.message.detail ?? m.message.title;
    if (m.message.detail) for (const b of m.message.detail.split(/\n\s*\n/).map(x => x.trim()).filter(Boolean)) para(b);
  } else if (m.kind === 'promesse' && m.pledge) {
    const who = m.pledge.partner || m.partner.name;
    subject = `Votre promesse de don pour l’app du club · ${club}`;
    sub = `L’app du club, offerte par ${who}`;
    pre = `${euros(m.pledge.amount_cents)} pour la saison ${m.pledge.season}`;
    para(`Merci encore d’offrir l’app du club pour la saison ${m.pledge.season} : depuis le ${longDay(m.pledge.since).replace(/^\S+ /, '')}, « L’app du club est offerte par ${who} » s’affiche chez chaque membre.`);
    para(`Votre promesse de don de ${euros(m.pledge.amount_cents)} n’est pas encore réglée. Pour le règlement (virement ou chèque), répondez simplement à cet e-mail : le club vous dit comment faire.`);
    para('Votre reçu fiscal suit dès que le club a noté le règlement.');
  } else if (m.kind === 'espace' && m.space) {
    const link = `${base}/p/${m.space.token}`;
    subject = `Le lien de votre espace partenaire · ${club}`;
    sub = 'Votre espace partenaire';
    pre = 'Ouvrez-le une fois sur le téléphone qui scanne les QR des membres';
    para(`Vous avez demandé le lien de votre espace partenaire ${ofClub(club)}, en scannant le QR d’un membre.`);
    para('Ouvrez-le une fois sur le téléphone qui scanne les QR des membres : ensuite, ce téléphone est reconnu, et « Valider » s’affiche à chaque scan.');
    htmlParts.push(`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px"><tr><td>
      <a href="${esc(link)}" style="display:block;text-align:center;padding:14px 10px;border-radius:12px;background:${c.fill};color:${c.ink};font-weight:700;font-size:16px;text-decoration:none">Ouvrir mon espace</a></td></tr></table>`);
    textParts.push(`Ouvrir mon espace : ${link}`);
    small('Ce lien est personnel : il ouvre votre espace sans mot de passe. Ne le transférez pas.');
  }

  // --- La signature ---
  const sign = signer ? `${signer}, ${title ? `${title} ${ofClub(club)}` : forClub(club)}` : `Le bureau ${ofClub(club)}`;
  htmlParts.push(`<p style="margin:0">Bien cordialement,</p><p style="margin:2px 0 4px">${signer ? `<b>${esc(signer)}</b>${esc(sign.slice(signer.length))}` : esc(sign)}</p>`);
  textParts.push(`Bien cordialement,\n${sign}`);
  if (m.replyTo) {
    const line = `Une question ? Répondez simplement à cet e-mail : il arrive à ${signer || `au bureau ${ofClub(club)}`}.`.replace('à au bureau', 'au bureau');
    htmlParts.push(`<p style="margin:0;font-size:13px;color:#5C6472">${esc(line)}</p>`);
    textParts.push(line);
  }

  // --- L'en-tête, le pied, la page ---
  const mark = logo.src
    ? `<img src="${esc(logo.src)}" width="58" height="58" alt="" style="display:block;width:58px;height:58px;border-radius:50%;background:#FFFFFF;border:0;object-fit:cover">`
    : `<div style="width:58px;height:58px;border-radius:50%;background:#FFFFFF;color:${c.text};font-family:${DISPLAY};font-size:20px;line-height:58px;text-align:center">${esc(monogram(club))}</div>`;
  const where = [club, m.club.city ?? ''].filter(Boolean).join(' · ');
  const inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;margin:0 auto;background:#FFFFFF;border-radius:16px;overflow:hidden;font-family:${FONT};color:#1D2128">
  <tr><td style="background:${c.fill};color:${c.ink};padding:22px 24px">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding-right:14px">${mark}</td>
      <td><p style="margin:0;font-family:${DISPLAY};font-size:24px;line-height:1.05;text-transform:uppercase;color:${c.ink}">${esc(club)}</p>
      <p style="margin:5px 0 0;font-size:14px;color:${c.ink}">${esc(sub)}</p></td></tr></table></td></tr>
  <tr><td style="padding:24px;font-size:15.5px;line-height:1.55">${htmlParts.join('\n')}</td></tr>
  <tr><td style="padding:16px 24px 20px;background:#F5F6F8;border-top:1px solid #E4E7EC;font-size:12px;line-height:1.6;color:#6B7380">${esc(where)}<br>
    Envoyé avec Arceau, l’app des clubs de basket · <a href="${esc(safeUrl(unsubscribe))}" style="color:#4A5260">Ne plus recevoir ces messages</a></td></tr>
</table>`;
  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light"><title>${esc(subject)}</title></head>
<body style="margin:0;padding:0;background:#EEF0F3">
<div style="display:none;max-height:0;overflow:hidden;opacity:0">${esc(pre)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#EEF0F3"><tr><td style="padding:18px 12px 28px">
${inner}
</td></tr></table></body></html>`;
  const footer = `\n\n—\n${where}\nEnvoyé avec Arceau, l’app des clubs de basket.\nNe plus recevoir ces messages : ${unsubscribe}`;
  return { subject, fromName: `${club} via Arceau`, html, inner, text: textParts.join('\n\n'), footer, unsubscribe, answer, inline: logo.inline ? [logo.inline] : [] };
}

/** Ce que rend la file d'envoi (partner_mail_outbox, réservé par la base pour push-dispatch). */
export interface PartnerOutMail {
  id: string; kind: PartnerMailKind; token: string; to: string; reply_to: string | null;
  signer: { name: string | null; title: string | null };
  club: { name: string; color: string | null; logo: string | null; city: string | null };
  partner: { name: string; civility: string | null; contact: string | null };
  invitation: (Omit<NonNullable<PartnerMailIn['invitation']>, 'mic'> & { mic: boolean | null; competition_name?: string | null; presented?: boolean }) | null;
  news: { title: string; body: string } | null;
  message: { title: string; detail: string | null } | null;
  pledge: { amount_cents: number; season: string; since: string; partner?: string } | null;
  space?: { token: string } | null;
  demo: boolean;
}
/** La ligne de la file → ce que le rendu attend. */
export function fromOutbox(m: PartnerOutMail): PartnerMailIn {
  return {
    kind: m.kind, token: m.token, club: m.club, partner: m.partner, signer: m.signer, replyTo: !!m.reply_to,
    invitation: m.invitation ? { ...m.invitation, gifts: m.invitation.gifts ?? [], mic: !!m.invitation.mic } : null,
    news: m.news, message: m.message, pledge: m.pledge, space: m.space ?? null,
  };
}

/** Une adresse d'exemple ou de démonstration (aussi .example) : jamais d'envoi réel. */
export const isFakePartnerAddress = (to: string): boolean =>
  /@([^@]*\.)?(arceau\.local|arceau\.test|exemple\.(fr|com|org)|example\.(com|org|net))$/i.test(to.trim())
  || /\.(local|test|invalid|example|localhost)$/i.test(to.trim()) || /@(example|exemple)\./i.test(to.trim());
